import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AbilityVectorResponse } from '../../services/caffaService';
import CaffaInfoModal from './CaffaInfoModal';
import './CaffaAbilityCard.css';
import './CaffaInfoModal.css';

interface Props {
    vector: AbilityVectorResponse | null;
}

const CEFR_COLORS: Record<string, string> = {
    A1: '#22c55e',
    A2: '#84cc16',
    B1: '#eab308',
    B2: '#f97316',
    C1: '#ef4444',
    C2: '#dc2626',
};

const STABLE_SAMPLE_COUNT = 5;

const CaffaAbilityCard: React.FC<Props> = ({ vector }) => {
    const { t } = useTranslation('knowledgeGraph');
    const [infoOpen, setInfoOpen] = useState(false);
    const [reEvalOpen, setReEvalOpen] = useState(false);

    const hasSample = !!vector?.exists && (vector?.sampleCount ?? 0) > 0;
    const sampleCount = vector?.sampleCount ?? 0;
    const stabilizing = hasSample && sampleCount < STABLE_SAMPLE_COUNT;
    const remainingToStabilize = Math.max(0, STABLE_SAMPLE_COUNT - sampleCount);

    useEffect(() => {
        if (!reEvalOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setReEvalOpen(false);
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [reEvalOpen]);

    const cefr = vector?.cefrEstimate ?? null;
    const cefrColor = cefr ? CEFR_COLORS[cefr] ?? '#667eea' : '#94a3b8';

    const dims: Array<{ key: string; score: number | null; hint: string }> = [
        { key: 'complexity', score: vector?.complexity ?? null, hint: t('caffa.dimHint.complexity') },
        { key: 'accuracy', score: vector?.accuracy ?? null, hint: t('caffa.dimHint.accuracy') },
        { key: 'fluency', score: vector?.fluency ?? null, hint: t('caffa.dimHint.fluency') },
        {
            key: 'functional_adequacy',
            score: vector?.functional_adequacy ?? null,
            hint: t('caffa.dimHint.functional_adequacy'),
        },
    ];

    return (
        <section className="caffa-ability-card">
            <header className="caffa-ability-card__header">
                <div className="caffa-ability-card__heading">
                    <div className="caffa-ability-card__title-row">
                        <h3 className="caffa-ability-card__title">{t('caffa.title')}</h3>
                        <button
                            type="button"
                            className="caffa-ability-card__info-btn"
                            onClick={() => setInfoOpen(true)}
                            aria-label={t('caffa.infoTooltip')}
                            title={t('caffa.infoTooltip')}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                        </button>
                    </div>
                    <p className="caffa-ability-card__subtitle">{t('caffa.subtitle')}</p>
                </div>
                {hasSample && cefr && (
                    <div
                        className="caffa-ability-card__cefr"
                        style={{ borderColor: cefrColor }}
                    >
                        <span
                            className="caffa-ability-card__cefr-level"
                            style={{ color: cefrColor }}
                        >
                            {cefr}
                        </span>
                        <span className="caffa-ability-card__cefr-label">
                            {t('caffa.latestCefr')}
                        </span>
                    </div>
                )}
            </header>

            {!hasSample ? (
                <div className="caffa-ability-card__empty">
                    <p>{t('caffa.emptyDescription')}</p>
                    <Link
                        className="caffa-ability-card__cta caffa-ability-card__cta--primary"
                        to="/writing"
                    >
                        {t('caffa.ctaStart')}
                    </Link>
                </div>
            ) : (
                <>
                    <ul className="caffa-ability-card__bars">
                        {dims.map((d) => (
                            <li className="caffa-ability-card__bar-row" key={d.key}>
                                <div className="caffa-ability-card__bar-label">
                                    <span className="caffa-ability-card__bar-name">
                                        {t(`caffa.dim.${d.key}`)}
                                    </span>
                                    <span className="caffa-ability-card__bar-score">
                                        {d.score != null ? d.score.toFixed(1) : '—'}
                                    </span>
                                </div>
                                <div className="caffa-ability-card__bar-track">
                                    <div
                                        className="caffa-ability-card__bar-fill"
                                        style={{
                                            width: `${Math.max(0, Math.min(100, d.score ?? 0))}%`,
                                        }}
                                    />
                                </div>
                                <p className="caffa-ability-card__bar-hint">{d.hint}</p>
                            </li>
                        ))}
                    </ul>

                    <footer className="caffa-ability-card__footer">
                        <div className="caffa-ability-card__sample">
                            {stabilizing ? (
                                <span>
                                    {t('caffa.stabilizing', {
                                        remaining: remainingToStabilize,
                                        total: STABLE_SAMPLE_COUNT,
                                    })}
                                </span>
                            ) : (
                                <span>{t('caffa.stable', { samples: sampleCount })}</span>
                            )}
                        </div>
                        <button
                            type="button"
                            className="caffa-ability-card__cta"
                            onClick={() => setReEvalOpen(true)}
                        >
                            {t('caffa.ctaMore')}
                        </button>
                    </footer>
                </>
            )}

            <CaffaInfoModal isOpen={infoOpen} onClose={() => setInfoOpen(false)} />

            {reEvalOpen && (
                <div
                    className="caffa-info-overlay"
                    onClick={() => setReEvalOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        className="caffa-info-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="caffa-info-close"
                            onClick={() => setReEvalOpen(false)}
                            aria-label={t('caffa.info.close')}
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>

                        <h2 className="caffa-info-title">
                            {t('caffa.reEvalModal.title')}
                        </h2>
                        <p className="caffa-info-desc">
                            {stabilizing
                                ? t('caffa.stabilizing', {
                                      remaining: remainingToStabilize,
                                      total: STABLE_SAMPLE_COUNT,
                                  })
                                : t('caffa.stable', { samples: sampleCount })}
                        </p>
                        <p className="caffa-info-desc">
                            {t('caffa.reEvalModal.description')}
                        </p>

                        <div className="caffa-info-actions">
                            <Link
                                to="/writing"
                                className="caffa-info-cta"
                                onClick={() => setReEvalOpen(false)}
                            >
                                {t('caffa.reEvalModal.cta')}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default CaffaAbilityCard;
